"use client";
import { FormProvider, Path } from "react-hook-form";
import { z } from "zod";
import Image from "next/image";

import VehicleDetails from "./VehicleDetails";
import OwnerDriverInfo from "./OwnerDriverInfo";
import InspectionWorkflow from "./InspectionWorkflow";
import InvoiceSetupForm from "./InvoiceSetupForm";
import InsuranceProtectionCard from "./InsuranceProtectionCard";
import { useTripAudit } from "../../../contexts/TripAuditContext";
import { handleStart } from "../utils/helper";
export const formSchema = z.object({
  vehicle: z.object({
    make: z.string().min(1, "Car make is required"),
    model: z.string().min(1, "Car model is required"),
    year: z
      .string()
      .min(1, "Year is required")
      .regex(/^\d{4}$/, "Year must be a 4-digit number")
      .refine(
        (val) => {
          const year = Number(val);
          const currentYear = new Date().getFullYear();
          return year >= 1900 && year <= currentYear + 5;
        },
        {
          message: `Year must be between 1900 and ${new Date().getFullYear() + 1}`,
        },
      ),
    fuelType: z.enum(["Gasoline", "Electric", "Hybrid"]),
    color: z.string().optional(),
    vin: z.string().optional(),
  }),
  owner: z.object({
    fullName: z.string().min(1, "Owner name is required"),
    email: z.string().email("Invalid email").optional(),
    phone: z.string().min(10, "Invalid phone").optional(),
    address: z.string().optional(),
  }),
  driver: z
    .object({
      sameAsOwner: z.boolean().optional(),
      fullName: z.string().optional(),
      email: z.string().email().optional(),
      phone: z.string().min(10).optional(),
      license: z.string().optional(),
      address: z.string().optional(),
    })
    .refine((data) => data.sameAsOwner || data.fullName, {
      message: "Driver name is required if not same as owner",
      path: ["fullName"],
    }),
  inspection: z.object({
    plan: z.enum(["Single", "Dual", "Invoice"]),
    access_limit_time: z.enum(["30", "90", "180", "365"]).optional(),
    license_verification: z.boolean(),
  }),
  // Add these for InsuranceProtectionCard
  trip: z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  }),
  invoice: z
    .object({
      currency: z.enum(["USD", "EUR", "GBP"]).optional(),
      is_primary_invoice: z.boolean().optional(),
      trip_charge: z
        .number()
        .nonnegative("Trip charge must be a non-negative number")
        .optional(),
      security_deposit: z.number().nonnegative().optional(),

      is_trip_milage_limit: z.boolean().optional(),
      trip_milage_limit: z.number().int().nonnegative().optional(),
      excess_milage_fee: z.number().nonnegative().optional(),

      is_additional_charges: z.boolean().optional(),
      standard_cleaning_charge: z.number().nonnegative().optional(),

      is_late_return_fee: z.boolean().optional(),
      late_return_fee: z.number().nonnegative().optional(),
      return_datetime: z.string().optional(),
      fuel_level: z.string().optional(),
      is_verify_drivers_license: z.boolean().optional(),
      is_age_limit: z.boolean().optional(),
      age_limit: z.number().int().min(18).optional(),
      driver_charge: z.number().nonnegative().optional(),
    })
    .superRefine((data, ctx) => {
      // Primary invoice
      if (data.is_primary_invoice) {
        if (data.trip_charge === undefined || data.trip_charge === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Drive charge is required",
            path: ["trip_charge"],
          });
        }
        if (
          data.security_deposit === undefined ||
          data.security_deposit === null
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Security deposit is required",
            path: ["security_deposit"],
          });
        }
      }

      // Mileage limit
      if (data.is_trip_milage_limit) {
        if (
          data.trip_milage_limit === undefined ||
          data.trip_milage_limit === null
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Drive mileage limit is required",
            path: ["trip_milage_limit"],
          });
        }
        if (
          data.excess_milage_fee === undefined ||
          data.excess_milage_fee === null
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Excess mileage fee is required",
            path: ["excess_milage_fee"],
          });
        }
      }

      // Late return fee
      if (data.is_late_return_fee) {
        if (!data.return_datetime) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Return date and time is required",
            path: ["return_datetime"],
          });
        }
        if (
          data.late_return_fee === undefined ||
          data.late_return_fee === null
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Late return fee is required",
            path: ["late_return_fee"],
          });
        }
      }

      // Additional charges
      if (data.is_additional_charges) {
        if (
          data.standard_cleaning_charge === undefined ||
          data.standard_cleaning_charge === null
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Standard cleaning charge is required",
            path: ["standard_cleaning_charge"],
          });
        }
        if (!data.fuel_level) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Fuel level is required",
            path: ["fuel_level"],
          });
        }
      }
    })
    .optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function InspectionPage() {
  const {
    methods,
    showInsurannceCover,
    setShowInsurannceCover,
    showInvoiceForm,
    setShowInvoiceForm,
    currentStep,
    setCurrentStep,
  } = useTripAudit();

  const { trigger } = methods;
  const scrollToCurrentStep = () => {
    // Mobile → scroll to the steps section
    if (window.innerWidth < 768) {
      document.getElementById("inspection-steps")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      // Desktop → keep the original behavior
      handleStart();
    }
  };
  const handleNext = async () => {
    const fieldsToValidate: Path<FormData>[] =
      currentStep === 1
        ? ["vehicle"]
        : currentStep === 2
          ? ["owner", "driver"]
          : ["inspection"];

    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setCurrentStep((prev) => prev + 1);
      scrollToCurrentStep();
    }
  };

  const handleStepClick = async (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step);
      scrollToCurrentStep();
      return;
    }

    for (let i = currentStep; i < step; i++) {
      let fields: Path<FormData>[] = [];
      if (i === 1) fields = ["vehicle"];
      else if (i === 2) fields = ["owner", "driver"];
      else if (i === 3) fields = ["inspection"];

      const isValid = await trigger(fields);
      if (!isValid) return;
    }
    setCurrentStep(step);
    scrollToCurrentStep();
  };

  const steps = [
    {
      title: "Enter vehicle Details",
      component: <VehicleDetails onNext={handleNext} />,
      desc: "Select the vehicle make, ,model and years information.",
    },
    {
      title: "Enter owner and driver details",
      component: <OwnerDriverInfo onNext={handleNext} />,
      desc: "Fill the needed details such as name, email and phone number",
    },
    {
      title: "Select your stamp",
      component: (
        <InspectionWorkflow
          setShowInvoiceForm={setShowInvoiceForm}
          setShowInsurannceCover={setShowInsurannceCover}
        />
      ),
      desc: "Pick your stamp type and proceed with one time checkout",
    },
  ];

  return (
    <FormProvider {...methods}>
      <div className="bg-white">
        <div
          className={`flex max-w-300 flex-col md:flex-row items-center mx-auto w-full px-5 ${showInsurannceCover ? "p-5" : " py-15 md:py-20"}`}
        >
          {/* Left Sidebar - Hide when showing Insurance or Invoice */}
          {!showInsurannceCover && !showInvoiceForm && (
            <div className="md:w-1/2 w-full">
              {/* Your existing sidebar content */}
              <p className="text-[#0336BC] text-[18px] font-openSans font-bold mb-[5px] md:mb-[10px]">
                No codes,apps, or subscriptions needed
              </p>
              <h1 className="text-[36px] font-bold text-[#04082C] font-montserrat leading-[1.1] mb-[10px] md:mb-5">
                Handover your vehicle with a proof{" "}
                {/* <br className="hidden md:block" /> */}
                <span className="text-[#0336BC]">
                  in three simplified steps.
                </span>
              </h1>
              <p className="text-[#7D7D7D] text-[16px] font-openSans leading-6">
                Simply purchase a one-time or two-time inspection workflow...
              </p>

              <div
                id="inspection-steps"
                className="mt-8 flex items-center gap-5"
              >
                <Image
                  src="/images/trip-audit/3-line.svg"
                  alt="Logo"
                  width={65}
                  height={100}
                />
                <div className="space-y-7.5 md:space-y-16.75 w-full md:max-w-103.75">
                  {steps.map((stepItem, index) => (
                    <div
                      key={index}
                      onClick={() => handleStepClick(index + 1)}
                      className={`flex items-center rounded-md px-5 py-4 gap-5 cursor-pointer shadow-[0_4px_5px_0_rgba(0,0,0,0.05)] ${
                        currentStep === index + 1
                          ? "border border-[#2D65F2] bg-[#F5F9FC]"
                          : ""
                      }`}
                    >
                      <div className="rounded-full bg-white border border-[#2D65F2] text-center w-8 h-8 flex items-center justify-center">
                        <span className="text-[12px] font-bold text-[#2D65F2]">
                          {index + 1}
                        </span>
                      </div>
                      <div>
                        <p className="text-[#333] md:text-[18px] font-bold">
                          {stepItem.title}
                        </p>
                        <p className="text-[14px] md:text-[16px] text-[#7D7D7D]">
                          {stepItem.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!showInsurannceCover && !showInvoiceForm && (
            <div className="border-r h-[600px] mx-[60px] border-[#DFDFDF] hidden md:block" />
          )}

          {/* Right Content */}
          <div
            className={`${showInvoiceForm || showInsurannceCover ? "w-full" : "md:w-1/2 w-full"}`}
          >
            {currentStep === 3 && showInvoiceForm ? (
              <InvoiceSetupForm />
            ) : currentStep === 3 && showInsurannceCover ? (
              <InsuranceProtectionCard />
            ) : (
              steps[currentStep - 1].component
            )}
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
